import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import { CheckIcon, DownloadIcon } from '../components/Icons';
import { services } from '../data';
import { getPageMeta } from '../utils/seoHelmet';

const Services = () => {
    const location = useLocation();
    const [visibleSections, setVisibleSections] = useState({});
    const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
    const [isUseCasesVisible, setIsUseCasesVisible] = useState(false);
    const sectionRefs = useRef([]);
    const howItWorksRef = useRef(null);
    const useCasesRef = useRef(null);

    // Scroll animations for each service section
    useEffect(() => {
        const observers = [];
        
        sectionRefs.current.forEach((ref, index) => {
            if (!ref) return;
            
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        setVisibleSections(prev => ({
                            ...prev,
                            [index]: entry.isIntersecting
                        }));
                    });
                },
                { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
            );
            
            observer.observe(ref);
            observers.push(observer);
        });

        // How It Works observer
        const howItWorksObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsHowItWorksVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        );
        if (howItWorksRef.current) howItWorksObserver.observe(howItWorksRef.current);
        observers.push(howItWorksObserver);

        // Use Cases observer
        const useCasesObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsUseCasesVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        );
        if (useCasesRef.current) useCasesObserver.observe(useCasesRef.current);
        observers.push(useCasesObserver);

        return () => observers.forEach(obs => obs.disconnect());
    }, []);

    // Smooth scroll to section when navigating with hash (e.g., /services#training)
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location.hash]);

    const implementationSteps = [
        {
            number: '01',
            title: 'Discovery & Alignment',
            description: 'We begin by understanding your organization\'s objectives, challenges, and context—covering business needs, risk considerations, and compliance expectations. This phase ensures every engagement starts with clarity and shared direction.'
        },
        {
            number: '02',
            title: 'Planning & Design',
            description: 'Based on our findings, we develop a tailored roadmap that defines scope, responsibilities, and success criteria. Whether for AI enablement, ISO frameworks, or security platforms, our designs prioritize practicality, governance, and sustainability.'
        },
        {
            number: '03',
            title: 'Implementation & Enablement',
            description: 'We support structured implementation through clear documentation, hands-on guidance, and knowledge transfer—ensuring your teams are equipped to operate and sustain the solution effectively.'
        },
        {
            number: '04',
            title: 'Review & Continuous Improvement',
            description: 'We stay engaged beyond delivery, providing support, monitoring, and optimization to ensure solutions remain relevant, compliant, and aligned with evolving business needs.'
        }
    ];

    const useCases = [
        {
            title: 'Responsible AI Adoption',
            subtitle: 'For Corporate & Professional Teams',
            description: 'Helping organizations build AI literacy and practical capability—ensuring teams understand how to apply AI tools responsibly, ethically, and in alignment with governance expectations.',
            image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
            applications: [
                'AI training for leadership and business teams',
                'Task automation and productivity enablement',
                'Responsible AI and governance awareness'
            ]
        },
        {
            title: 'Quality & Compliance Readiness',
            subtitle: 'For SMEs & Growing Enterprises',
            description: 'Supporting organizations in building ISO 9001-aligned systems that improve process consistency, accountability, and audit readiness.',
            image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop',
            applications: [
                'Process mapping and documentation',
                'Risk-based thinking and improvement planning',
                'Internal audit and certification readiness'
            ]
        },
        {
            title: 'Information Security & Risk Management',
            subtitle: 'For Regulated & Security-Conscious Organizations',
            description: 'Helping organizations strengthen information security governance through ISO/IEC 27001-aligned frameworks and risk-based controls.',
            image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
            applications: [
                'ISMS scope definition and gap assessment',
                'Risk assessment and treatment',
                'Security awareness and governance alignment'
            ]
        },
        {
            title: 'Intelligent Security & Surveillance',
            subtitle: 'For Commercial & Enterprise Environments',
            description: 'Enhancing physical security operations with AI-powered monitoring—supporting proactive prevention, faster response, and compliant data handling.',
            image: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600&h=400&fit=crop',
            applications: [
                'Real-time threat detection',
                'Behavioral anomaly analysis',
                'Enterprise security system integration'
            ]
        }
    ];

    const meta = getPageMeta('services');

    return (
        <main>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <meta name="keywords" content={meta.keywords} />
                <meta name="robots" content={meta.robots} />
                <link rel="canonical" href={meta.canonical} />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.ogImage} />
                <meta property="og:url" content={meta.canonical} />
            </Helmet>
            {/* Page Hero */}
            <section 
                className="page-hero page-hero--dark" 
                style={{ 
                    backgroundImage: `linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(15, 15, 35, 0.95)), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=600&fit=crop&q=80')` 
                }}
            >
                <div className="container">
                    <span className="page-hero__eyebrow">Our Services</span>
                    <h1 className="page-hero__title">Enterprise-Grade Solutions</h1>
                    <p className="page-hero__subtitle">
                        AI and compliance solutions designed for organizations 
                        that require accountability, security, and proven results.
                    </p>
                </div>
            </section>

            {/* Service Details */}
            <div className="services-page">
                {services.map((service, index) => (
                    <section 
                        key={service.id}
                        id={service.id}
                        className={`service-detail ${index % 2 === 1 ? 'service-detail--alt' : ''} ${visibleSections[index] ? 'service-detail--visible' : ''}`}
                        ref={el => sectionRefs.current[index] = el}
                    >
                        <div className="service-detail__bg"></div>
                        <div className="container service-detail__container">
                            <div className="service-detail__content">
                                <span className="service-detail__number">{String(index + 1).padStart(2, '0')}</span>
                                <span className="service-detail__eyebrow">{service.eyebrow}</span>
                                <h2 className="service-detail__title">{service.title}</h2>
                                <p className="service-detail__text">{service.description}</p>
                                <p className="service-detail__text">{service.longDescription}</p>
                                <div className="service-detail__features">
                                    {service.features.map((feature, idx) => (
                                        <div key={idx} className="service-detail__feature">
                                            <span className="service-detail__feature-icon">
                                                <CheckIcon size={14} />
                                            </span>
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                                <div className="service-detail__actions">
                                    <Button to="/contact">
                                        Get Started
                                    </Button>
                                    {service.resourcePdf && (
                                        <a 
                                            href={service.resourcePdf.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="service-detail__pdf-btn"
                                            title={service.resourcePdf.title}
                                        >
                                            <DownloadIcon size={16} />
                                            View PDF
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="service-detail__image">
                                <img src={service.image} alt={service.title} />
                                <div className="service-detail__image-accent"></div>
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            {/* How It Works Section */}
            <section 
                className={`section how-it-works ${isHowItWorksVisible ? 'how-it-works--visible' : ''}`}
                ref={howItWorksRef}
            >
                <div className="container">
                    <div className="how-it-works__header">
                        <span className="how-it-works__eyebrow">Our Approach</span>
                        <h2 className="how-it-works__title">How It Works</h2>
                        <p className="how-it-works__subtitle">A structured approach for intelligent, compliant, and secure implementation</p>
                        <p className="how-it-works__description">We follow a clear, practical methodology to help organizations adopt solutions with confidence—ensuring alignment with business goals, governance requirements, and operational realities.</p>
                    </div>
                    <div className="how-it-works__timeline">
                        {implementationSteps.map((step, index) => (
                            <div key={index} className="how-it-works__step">
                                <div className="how-it-works__step-number">{step.number}</div>
                                <div className="how-it-works__step-content">
                                    <h3 className="how-it-works__step-title">{step.title}</h3>
                                    <p className="how-it-works__step-description">{step.description}</p>
                                </div>
                                {index < implementationSteps.length - 1 && (
                                    <div className="how-it-works__connector"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use Cases Section */}
            <section 
                className={`section use-cases ${isUseCasesVisible ? 'use-cases--visible' : ''}`}
                ref={useCasesRef}
            >
                <div className="container">
                    <div className="use-cases__header">
                        <span className="use-cases__eyebrow">Application Framework</span>
                        <h2 className="use-cases__title">Industry Use Cases</h2>
                        <p className="use-cases__subtitle">How Organizations Apply Phoenix AI Solutions</p>
                        <p className="use-cases__description">See how organizations across industries use our AI enablement, ISO consultancy, and security platforms to strengthen governance, improve efficiency, and manage risk responsibly.</p>
                    </div>
                    <div className="use-cases__grid">
                        {useCases.map((useCase, index) => (
                            <div key={index} className="use-case-card">
                                <div className="use-case-card__image">
                                    <img src={useCase.image} alt={useCase.title} />
                                </div>
                                <div className="use-case-card__content">
                                    <h3 className="use-case-card__title">{useCase.title}</h3>
                                    <p className="use-case-card__subtitle">{useCase.subtitle}</p>
                                    <p className="use-case-card__description">{useCase.description}</p>
                                    <div className="use-case-card__applications">
                                        <h4 className="use-case-card__applications-label">Applications:</h4>
                                        <ul className="use-case-card__applications-list">
                                            {useCase.applications.map((app, idx) => (
                                                <li key={idx} className="use-case-card__application-item">{app}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </main>
    );
};

export default Services;
